import { graphql } from 'gatsby';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { Box, Heading } from 'lucid-components-react';

import { mqMin } from 'theme/base';
import { Drupal_ParagraphFlexiStyledMultiSections } from 'generated/graphql';
import FourAcross from './variants/four-across';
import ThreeAcross from './variants/three-across';
import SixScattered from './variants/six-scattered';
import ThreeSlideshow from './variants/three-slideshow';
import ThreeOffset from './variants/three-offset';
import { LucidTheme } from 'types/types';
import FlourishedHeading from 'components/flourished-heading/flourished-heading';
import BackAndForth from './variants/back-and-forth';
import { Text } from 'theme/typography';
import { getProcessedValue } from 'utils/helpers';

interface Props {
  entity: Drupal_ParagraphFlexiStyledMultiSections;
}

const componentMapping: Record<string, any> = {
  'Four across': FourAcross,
  'Three across': ThreeAcross,
  'Six scattered': SixScattered,
  'Three slideshow': ThreeSlideshow,
  'Three offset': ThreeOffset,
  'Back and forth': BackAndForth
};

const FlexiStyledMultiSections = ({
  entity: {
    fieldHeader,
    fieldSubheader,
    fieldFlexiBody,
    fieldFlexiMultiSectionVarian,
    fieldSections
  }
}: Props) => {
  const Variant = componentMapping[fieldFlexiMultiSectionVarian as string];
  return (
    <Box
      css={(theme: LucidTheme) => ({
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        marginBottom: theme.space[6]
      })}
      gridColumn={'1 / -1'}
    >
      <Box flex="0 1 100%" mb={9}>
        <Box
          css={{
            flex: '0 1 100%',
            maxWidth: '100%'
          }}
        >
          {fieldHeader && (
            <FlourishedHeading
              as="h2"
              css={{
                [mqMin.tabletLandscape]: {
                  flex: '0 1 45%',
                  maxWidth: '45%'
                }
              }}
              variant="subheadL700"
            >
              {fieldHeader}
            </FlourishedHeading>
          )}
        </Box>
        {fieldSubheader && (
          <Box
            css={{
              flex: '0 1 100%',
              maxWidth: '100%'
            }}
          >
            <Heading as="h2">{fieldSubheader}</Heading>
          </Box>
        )}
        {fieldFlexiBody && (
          <Text as="div" css={{ maxWidth: '580px' }} textStyles="bodyS400">
            {ReactHtmlParser(getProcessedValue(fieldFlexiBody))}
          </Text>
        )}
      </Box>
      {Variant ? <Variant entities={fieldSections} /> : null}
    </Box>
  );
};

export default FlexiStyledMultiSections;

export const query = graphql`
  fragment flexiStyledMultiSections on Drupal_ParagraphFlexiStyledMultiSections {
    fieldHeader
    fieldSubheader
    fieldFlexiBody {
      processed
    }
    fieldFlexiMultiSectionVarian
    fieldSections {
      entity {
        ...flexiSection
      }
    }
  }
`;
